import { useEffect, useState } from 'react';
import type { ReportReason } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { initialsOf } from '../../components/common';
import { relativeTime } from '../ally/ChatList';
import type { FeedPost, FeedLikeResult } from '../../lib/backend/types';
// Shared with ConversationView — one implementation of the locked state, since
// a second copy would be a second place to get the "never fetch it" rule wrong.
import { LockedMedia } from '../../components/LockedMedia';
import { CommentsSheet } from './CommentsSheet';

/**
 * One post.
 *
 * Delete and Report are mutually exclusive by construction — you get exactly
 * one of them, and which one depends on whether you wrote the post. Both
 * confirm INSIDE the card rather than through `confirm()`, which renders
 * unstyled in the Android WebView and reads like a browser error.
 */

/** The reasons a post can be reported for, in the order a reporter thinks of them. */
const REPORT_REASONS: { id: ReportReason; label: string }[] = [
  { id: 'harassment', label: 'Harassment or abuse' },
  { id: 'inappropriate-content', label: 'Inappropriate content' },
  { id: 'spam', label: 'Spam' },
  { id: 'safety-concern', label: 'Safety concern' },
  { id: 'fake-profile', label: 'Fake profile' },
  { id: 'other', label: 'Something else' },
];

export function PostCard({
  post,
  isPro,
  onDelete,
  onReport,
  onToggleLike,
}: {
  post: FeedPost;
  isPro: boolean;
  onDelete(post: FeedPost): Promise<void>;
  onReport(post: FeedPost, reason: ReportReason): Promise<void>;
  onToggleLike(post: FeedPost, on: boolean): Promise<FeedLikeResult>;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<'idle' | 'confirm-delete' | 'report'>('idle');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  /* ENGAGEMENT STATE IS LOCAL AND OPTIMISTIC.
     The heart has to move on the same frame as the tap — a like that waits for
     a round trip feels broken on a phone with two bars of signal — so the
     count and the filled state live here and are corrected afterwards. Absent
     counters mean a post written before engagement shipped: read as 0, never
     bare, or the card renders "NaN". */
  const [liked, setLiked] = useState(post.likedByMe === true);
  const [likeCount, setLikeCount] = useState(post.likeCount ?? 0);
  const [commentCount, setCommentCount] = useState(post.commentCount ?? 0);
  const [likeBusy, setLikeBusy] = useState(false);
  const [showComments, setShowComments] = useState(false);

  // A refetched page (pull to refresh, posting, paging) brings newer numbers
  // than the ones this card has been keeping. Server truth wins.
  useEffect(() => {
    setLiked(post.likedByMe === true);
    setLikeCount(post.likeCount ?? 0);
    setCommentCount(post.commentCount ?? 0);
  }, [post.likedByMe, post.likeCount, post.commentCount]);

  const toggleLike = async () => {
    if (likeBusy) return;
    const next = !liked;
    const revertLiked = liked;
    const revertCount = likeCount;

    setLiked(next);
    setLikeCount(Math.max(0, revertCount + (next ? 1 : -1)));
    setLikeBusy(true);
    setNote(null);
    try {
      // Reconciled to the server's own count rather than left on the guess —
      // other people are liking the same post while this one is open.
      const result = await onToggleLike(post, next);
      setLiked(result.liked);
      setLikeCount(result.likeCount);
    } catch {
      setLiked(revertLiked);
      setLikeCount(revertCount);
      setNote(t('feed.likeFailed'));
    } finally {
      setLikeBusy(false);
    }
  };

  // The author always sees their own attachment, whatever their tier. They
  // already have the file, so this leaks nothing, and locking someone out of
  // their own post the day their subscription lapses would be gratuitous.
  // storage.rules says exactly the same thing (`isOwner(uid) || isPro()`).
  const unlocked = isPro || post.mine;

  const run = async (fn: () => Promise<void>, failure: string) => {
    setBusy(true);
    setNote(null);
    try {
      await fn();
    } catch {
      setNote(failure);
      setBusy(false);
      setMode('idle');
    }
  };

  return (
    <div className="card fade-in" style={{ marginBottom: 10 }}>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="avatar" aria-hidden>
          {initialsOf(post.authorDisplayName || '?')}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="spread">
            <span className="h3">{post.authorDisplayName}</span>
            <span className="tiny faint">{relativeTime(post.createdAtISO, t)}</span>
          </div>

          {/* `pre-wrap` so a post the author broke into lines stays broken into
              lines, and `break-word` so an unbroken 500-character string cannot
              stretch the card off the screen. */}
          <div
            className="small"
            style={{ marginTop: 6, lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {post.text}
          </div>

          {post.media && <LockedMedia media={post.media} unlocked={unlocked} />}

          {/* No per-post caveat here, but NOT because photos are checked —
              they are not. No image check is implemented anywhere (the
              moderation provider exposes only check(text)), so every image is
              recorded `unchecked`. The honest statement belongs where someone
              is deciding what to post rather than repeated under every photo
              they scroll past, so it lives in PostComposer; the chat view says
              the same thing per clip because video carries it permanently. */}

          {note && (
            <div className="tiny danger" style={{ marginTop: 8 }}>
              {note}
            </div>
          )}

          {mode === 'idle' && (
            <div className="row" style={{ marginTop: 10, gap: 8 }}>
              {/* The heart carries its own state in `aria-pressed` rather than
                  in the glyph alone: ♥ and ♡ are one code point apart and a
                  screen reader announces neither as "liked". The count sits
                  outside the icon so it is read as a number, not decoration. */}
              <button
                className="chip"
                aria-pressed={liked}
                aria-label={`${liked ? t('feed.unlike') : t('feed.like')} — ${t('feed.likeCount', { n: likeCount })}`}
                onClick={() => void toggleLike()}
                style={liked ? { color: 'var(--accent-ink)', borderColor: 'var(--accent-line)' } : undefined}
              >
                <span aria-hidden>{liked ? '♥' : '♡'}</span>
                {likeCount > 0 && <span style={{ marginLeft: 6 }}>{likeCount}</span>}
              </button>

              <button
                className="chip"
                aria-label={t('feed.commentCount', { n: commentCount })}
                onClick={() => setShowComments(true)}
              >
                <span aria-hidden>💬</span>
                {commentCount > 0 && <span style={{ marginLeft: 6 }}>{commentCount}</span>}
              </button>

              {post.mine ? (
                <button className="chip" onClick={() => setMode('confirm-delete')}>
                  Delete
                </button>
              ) : (
                <button className="chip" onClick={() => setMode('report')}>
                  {t('msg.report')}
                </button>
              )}
            </div>
          )}

          {mode === 'confirm-delete' && (
            <div className="col" style={{ marginTop: 10 }}>
              <div className="tiny muted">
                Delete this post? It disappears for everyone
                {post.media ? ', and the file goes with it' : ''}. This can’t be undone.
              </div>
              <div className="row">
                <button
                  className="btn btn--ghost btn--small"
                  style={{ flex: 1 }}
                  disabled={busy}
                  onClick={() => setMode('idle')}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn btn--danger btn--small"
                  style={{ flex: 1 }}
                  disabled={busy}
                  onClick={() => run(() => onDelete(post), 'That didn’t delete. Try again.')}
                >
                  {busy ? <span className="spinner" /> : 'Delete'}
                </button>
              </div>
            </div>
          )}

          {mode === 'report' && (
            <div className="col" style={{ marginTop: 10 }}>
              <div className="tiny muted">
                What’s wrong with this post? Reports are private — {post.authorDisplayName} is
                never told who reported them.
              </div>
              <div className="row wrap">
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r.id}
                    className="chip"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await onReport(post, r.id);
                        setBusy(false);
                        setMode('idle');
                        setNote(null);
                      }, 'That report didn’t send. Try again.')
                    }
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <button className="btn btn--ghost btn--small" disabled={busy} onClick={() => setMode('idle')}>
                {t('common.cancel')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mounted only while open, so a feed of fifteen cards is not fifteen
          dialogs in the DOM — and so the sheet refetches on every open rather
          than showing a page that went stale hours ago. */}
      {showComments && (
        <CommentsSheet
          postId={post.id}
          postAuthorUid={post.authorUid}
          onClose={() => setShowComments(false)}
          onCountChange={(delta) => setCommentCount((n) => Math.max(0, n + delta))}
        />
      )}
    </div>
  );
}
